import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button, FluentProvider, webLightTheme } from "@fluentui/react-components";
import {
  Alert24Filled,
  Alert24Regular,
  // AppsAddIn24Regular, // used by the commented-out "Add apps" button
  Attach20Regular,
  Calendar24Filled,
  Video24Filled,
  Video24Regular,
  Calendar24Regular,
  Chat16Filled,
  Call20Regular,
  Call24Filled,
  Call24Regular,
  CallInbound16Regular,
  CallMissed16Regular,
  CallOutbound16Regular,
  Call20Filled,
  Chat20Regular,
  Filter20Regular,
  Open16Regular,
  People20Filled,
  Person20Filled,
  PersonAdd20Regular,
  Add16Regular,
  Chat24Filled,
  Chat24Regular,
  ChatMultiple24Regular,
  Checkmark16Regular,
  CheckmarkCircle16Regular,
  ChevronDown12Regular,
  CommentMultipleRegular,
  Compose20Regular,
  Dismiss20Regular,
  Emoji20Regular,
  Eye16Filled,
  MoreHorizontal20Regular,
  // MoreHorizontal24Regular, // used by the commented-out "More" button
  PanelLeft24Regular,
  PeopleAdd20Regular,
  Person20Regular,
  Pin20Regular,
  PinOff20Regular,
  Search20Regular,
  Send20Filled,
  Send20Regular,
  TextEditStyle20Regular,
  Add20Regular,
  Document20Regular,
  Settings20Regular,
  QuestionCircle20Regular,
  Phone20Regular,
} from "@fluentui/react-icons";
import type { FluentIcon } from "@fluentui/react-icons";
import "./InternalMessage.css"; // legacy file – still provides the floating chat button styling
import { IContact } from "../../../types/Contacts";
import { IChatMessage } from "../../../types/IChatMessages";
import { useSuiteAuth } from "./useSuiteAuth";
import TeamsCalls, { useCallLog } from "./Calls/Teamscalls";
import type { CallEntry } from "./Calls/Teamscalls";
import TeamsCalendar from "./Calenders/Teamscalendar";
import MeetingsPage from "./Meetings/MeetingsPage";
import { openMeeting } from "./Meetings/meetingUi";
import CallOverlay from "./Calls/CallOverlay";
import NotificationSettings from "./NotificationSettings";
import { calls as callActions } from "./spurt/calls";
import { chat } from "./spurt/chatStore";
import { notifications, useNotifications } from "./spurt/notifications";
import { onNavigate } from "./spurt/push";
import { useChatData } from "./spurt/useChatData";
import type { NotificationItem } from "./spurt/types";
import { PersonAvatar, fmtClock, fmtDivider, fmtHistoryDay, fmtStamp, fullName, useOutsideClose } from "./Shared/Teamsshared";
import styles from "./Internalmessage.module.css";
import sizeStyles from "./WindowSize.module.css";
import FloatingChatButton from "./Floating/Floatingchatbutton";

/* ------------------------------------------------------------------ *
 *  Internal communication – Microsoft Teams style.
 *  Rail: Activity · Chat · Calendar · Calls.  All data / websocket
 *  logic is the same as before; only the UI has been rebuilt.
 * ------------------------------------------------------------------ */

type Page = "activity" | "chat" | "calendar" | "meetings" | "calls";
type ListFilter = "all" | "chats" | "unread" | "contacts";
type ConvoTab = "chat" | "shared";

interface Convo {
  contact: IContact;
  preview: string;
  at: number;
  unread: number;
  mine: boolean;
}

interface ActItem {
  id: string;
  kind: "message" | "call" | "other";
  contact: IContact | null;
  name: string;
  image: string | null;
  online: boolean;
  title: string;
  sub: string;
  context: string;
  at: number;
  unread: boolean;
  callId?: string | undefined;
  notifId?: string | undefined;
  link?: string | undefined;
}

interface InternalCommunicationProps {
  /** controlled open state – pass this to open / close the chat from the parent */
  open?: boolean | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
  /** legacy prop, ignored */
  handleClick?: ((...args: any[]) => void) | undefined;
}

const EMOJIS = ["😀", "😂", "😊", "😍", "👍", "🙏", "🎉", "🔥", "👏", "😢", "😮", "🤝", "✅", "❤️", "💯", "👀"];

const ms = (iso: string | undefined): number => {
  const t = iso ? Date.parse(iso) : NaN;
  return isNaN(t) ? 0 : t;
};

const NO_MESSAGES: IChatMessage[] = [];

const RAIL: { key: Page; label: string; Icon: FluentIcon; IconOn: FluentIcon }[] = [
  { key: "activity", label: "Activity", Icon: Alert24Regular, IconOn: Alert24Filled },
  { key: "chat", label: "Chat", Icon: Chat24Regular, IconOn: Chat24Filled },
  { key: "calendar", label: "Calendar", Icon: Calendar24Regular, IconOn: Calendar24Filled },
  { key: "meetings", label: "Meetings", Icon: Video24Regular, IconOn: Video24Filled },
  { key: "calls", label: "Calls", Icon: Call24Regular, IconOn: Call24Filled },
];

const InternalCommunication: React.FC<InternalCommunicationProps> = ({ open, onOpenChange }) => {
  const [innerOpen, setInnerOpen] = useState(false);
  const isOpen = open !== undefined ? open : innerOpen;
  const setIsOpen = useCallback(
    (v: boolean) => {
      setInnerOpen(v);
      if (onOpenChange) onOpenChange(v);
    },
    [onOpenChange],
  );
  const { user, logout } = useSuiteAuth();
  /* ---------------- data (live from the Spurt backend) ---------------- */
  const meContact = useMemo<IContact>(
    () => ({ _id: user.userId, userId: Number(user.userId) || 0, firstName: user.firstName, lastName: user.lastName, email: user.email, phoneNumber: user.phone, profilePicture: user.profilePicture, status: true }),
    [user.userId, user.firstName, user.lastName, user.email, user.phone, user.profilePicture],
  );
  const { contactList, contactById, threads, unreadCounts, hasConversation, typingNames, hasMoreFor, dmOf } = useChatData(meContact);
  const directory = useMemo(() => contactList.map((c) => ({ name: `${c.firstName} ${c.lastName}`.trim(), email: c.email, userId: c._id })), [contactList]);
  const [selectedId, setSelectedId] = useState<string>();
  const selectedContact: IContact | undefined = selectedId ? contactById[selectedId] : undefined;
  const messages = selectedContact ? threads[selectedContact._id] || NO_MESSAGES : NO_MESSAGES;
  const notifItems = useNotifications((n) => n.items);
  const notifUnread = useNotifications((n) => n.unread);

  /* ---------------- ui state ---------------- */
  const [page, setPage] = useState<Page>("chat");
  const [listOpen, setListOpen] = useState(true);
  const [listFilter, setListFilter] = useState<ListFilter>("all");
  const [listSearch, setListSearch] = useState("");
  const [listSearchOn, setListSearchOn] = useState(false);
  const [favs, setFavs] = useState<string[]>([]);
  const [secOpen, setSecOpen] = useState<Record<string, boolean>>({ fav: true, chats: true });
  const [started, setStarted] = useState<IContact[]>([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [convoTab, setConvoTab] = useState<ConvoTab>("chat");
  const [convoSearch, setConvoSearch] = useState("");
  const [convoSearchOn, setConvoSearchOn] = useState(false);
  const [topQuery, setTopQuery] = useState("");
  const [pop, setPop] = useState<string | null>(null);
  const [toastMsg, setToastMsg] = useState<{ msg: string; id: number } | null>(null);
  const [speedReq, setSpeedReq] = useState(0);
  const [dialReq, setDialReq] = useState<{ contact: IContact; nonce: number; video?: boolean } | null>(null);
  const [actSel, setActSel] = useState<string | null>(null);
  const [actSearchOn, setActSearchOn] = useState(false);
  const [actSearch, setActSearch] = useState("");
  const [actFilter, setActFilter] = useState<"all" | "unread" | "messages" | "calls">("all");
  const [now, setNow] = useState(() => Date.now());

  const windowRef = useRef<HTMLDivElement>(null);
  const msgsRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const toastTimer = useRef<number | undefined>(undefined);
  const closePop = useCallback(() => setPop(null), []);
  useOutsideClose(pop, closePop);

  const { calls, setCalls } = useCallLog(contactList);

  /* tell the store which conversation is on screen, so new messages there are read straight away */
  const viewingChat = isOpen && page === "chat" && !!selectedContact;
  const viewingId = selectedContact?._id;
  useEffect(() => {
    chat.setWindow(viewingChat, viewingChat && viewingId ? dmOf[viewingId] ?? null : null);
  }, [viewingChat, viewingId, dmOf]);

  const toast = useCallback((msg: string) => {
    window.clearTimeout(toastTimer.current);
    setToastMsg({ msg, id: Date.now() });
    toastTimer.current = window.setTimeout(() => setToastMsg(null), 2400);
  }, []);
  useEffect(() => () => window.clearTimeout(toastTimer.current), []);

  /* clock for relative labels */
  useEffect(() => {
    if (!isOpen) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 60000);
    return () => window.clearInterval(id);
  }, [isOpen]);

  /* focus the window on open so Esc works straight away */
  useEffect(() => {
    if (isOpen && windowRef.current) windowRef.current.focus();
  }, [isOpen]);

  /* ---------------- derived ---------------- */
  const myName = fullName(user);

  const convos = useMemo<Convo[]>(() => {
    const byId: Record<string, Convo> = {};
    contactList.forEach((c) => {
      const list = threads[c._id];
      const last = list && list.length ? list[list.length - 1] : undefined;
      if (!last) return;
      byId[c._id] = { contact: c, preview: last.message, at: ms(last.timestamp), unread: unreadCounts[c._id] || 0, mine: last.senderId === user.userId };
    });
    started.forEach((c) => {
      if (!byId[c._id]) byId[c._id] = { contact: c, preview: "", at: 0, unread: unreadCounts[c._id] || 0, mine: false };
    });
    contactList.forEach((c) => {
      if (!byId[c._id] && hasConversation.has(c._id)) byId[c._id] = { contact: c, preview: "", at: 0, unread: unreadCounts[c._id] || 0, mine: false };
    });
    return Object.keys(byId)
      .map((k) => byId[k] as Convo)
      .sort((a, b) => b.at - a.at);
  }, [threads, contactList, unreadCounts, started, hasConversation, user.userId]);

  const totalUnread = convos.reduce((n, c) => n + c.unread, 0);
  const missedUnread = calls.filter((c) => c.direction === "missed" && !c.read).length;

  const ls = listSearch.trim().toLowerCase();
  const visibleConvos = convos.filter((c) => {
    if (listFilter === "unread" && c.unread === 0) return false;
    if (ls && `${fullName(c.contact)} ${c.preview}`.toLowerCase().indexOf(ls) === -1) return false;
    return true;
  });
  const favConvos = visibleConvos.filter((c) => favs.indexOf(c.contact._id) !== -1);
  const chatConvos = visibleConvos.filter((c) => favs.indexOf(c.contact._id) === -1);

  const contactGroups = useMemo(() => {
    const sorted = contactList
      .filter((c) => !ls || `${fullName(c)} ${c.email} ${c.phoneNumber}`.toLowerCase().indexOf(ls) !== -1)
      .slice()
      .sort((a, b) => fullName(a).localeCompare(fullName(b)));
    const groups: { letter: string; items: IContact[] }[] = [];
    sorted.forEach((c) => {
      const letter = (fullName(c).charAt(0) || "#").toUpperCase();
      const g = groups[groups.length - 1];
      if (g && g.letter === letter) g.items.push(c);
      else groups.push({ letter, items: [c] });
    });
    return groups;
  }, [contactList, ls]);

  /* ---------------- actions ---------------- */
  const openChat = useCallback(
    (c: IContact) => {
      setSelectedId(c._id);
      setPage("chat");
      setConvoTab("chat");
      setConvoSearch("");
      setConvoSearchOn(false);
      setStarted((s) => (s.some((x) => x._id === c._id) ? s : s.concat([c])));
      setPop(null);
      // make sure the conversation exists on the server, then show its newest messages
      chat
        .openDM(c._id)
        .then((convId) => chat.loadHistory(convId, true))
        .catch(() => toast("Couldn't open this chat. Check your connection."));
    },
    [toast],
  );

  const callContact = (c: IContact, video = false) => {
    setDialReq({ contact: c, nonce: Date.now(), video });
    setPage("calls");
    setPop(null);
  };

  const draft = selectedContact ? drafts[selectedContact._id] || "" : "";
  const setDraft = (v: string) => {
    if (!selectedContact) return;
    const id = selectedContact._id;
    setDrafts((d) => ({ ...d, [id]: v }));
    const convId = dmOf[id];
    if (v && convId) chat.typing(convId);
  };
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 140) + "px";
  }, [draft, selectedContact, page, convoTab]);

  const handleSendMessage = (content: string) => {
    const text = content.trim();
    if (!text || !selectedContact) return;
    const id = selectedContact._id;
    setDraft("");
    setPop(null);
    chat
      .openDM(id)
      .then((convId) => chat.send(convId, text))
      .catch(() => toast("Couldn't send. Check your connection."));
  };

  /* keep the newest message in view */
  useEffect(() => {
    const el = msgsRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length ? messages[messages.length - 1]?.messageId : "", selectedId, convoTab, page]);

  /* ---------------- chat message rows ---------------- */
  const isMine = (m: IChatMessage) => m.senderId === user.userId;

  type Row = { type: "day"; key: string; label: string } | { type: "group"; key: string; mine: boolean; name: string; image: string | null; stamp: string; items: IChatMessage[] };
  const rows = useMemo<Row[]>(() => {
    const cs = convoSearch.trim().toLowerCase();
    const list = messages
      .filter((m) => !m.isDeleted && (!cs || m.message.toLowerCase().indexOf(cs) !== -1))
      .slice()
      .sort((a, b) => ms(a.timestamp) - ms(b.timestamp));
    const out: Row[] = [];
    let lastDay = "";
    let cur: Extract<Row, { type: "group" }> | null = null;
    let curEnd = 0;
    list.forEach((m, i) => {
      const t = ms(m.timestamp);
      const day = new Date(t).toDateString();
      const mine = isMine(m);
      if (day !== lastDay) {
        lastDay = day;
        out.push({ type: "day", key: "d" + i, label: fmtDivider(t, now) });
        cur = null;
      }
      if (!cur || cur.mine !== mine || t - curEnd > 5 * 60000) {
        cur = {
          type: "group",
          key: "g" + i,
          mine,
          name: mine ? myName : fullName(m.sender),
          image: mine ? user.profilePicture || null : m.sender.profilePicture || null,
          stamp: fmtStamp(t, now),
          items: [],
        };
        out.push(cur);
      }
      cur.items.push(m);
      curEnd = t;
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, convoSearch, now, user.userId]);

  const lastMsg = messages.length ? messages[messages.length - 1] : undefined;
  const sharedFiles = messages.filter((m) => m.fileUrl);

  /* ---------------- activity feed ---------------- */
  const actItems = useMemo<ActItem[]>(() => {
    const items: ActItem[] = notifItems.map((n: NotificationItem) => {
      const contact = n.from ? contactById[n.from] || null : null;
      return {
        id: "n" + n.id,
        kind: n.category === "message" ? "message" : "other",
        contact,
        name: n.fromName || (contact ? fullName(contact) : n.title),
        image: contact?.profilePicture || null,
        online: contact?.status === true,
        title: n.title,
        sub: n.body || "",
        context: n.category === "message" ? "In chat with you" : n.category,
        at: n.createdMs,
        unread: n.readMs === 0,
        notifId: n.id,
        link: n.link,
      };
    });
    calls
      .filter((c) => c.direction === "missed")
      .forEach((c) => {
        items.push({
          id: "c" + c.id,
          kind: "call",
          contact: contactById[c.contactId] || null,
          name: c.name,
          image: c.image,
          online: c.online,
          title: `Missed call from ${c.name}`,
          sub: "Audio call",
          context: "",
          at: c.at,
          unread: !c.read,
          callId: c.id,
        });
      });
    return items.sort((a, b) => b.at - a.at);
  }, [notifItems, calls, contactById]);
  const asq = actSearch.trim().toLowerCase();
  const actShown = actItems.filter((a) => {
    if (asq && `${a.title} ${a.sub}`.toLowerCase().indexOf(asq) === -1) return false;
    return actFilter === "all" ? true : actFilter === "unread" ? a.unread : actFilter === "messages" ? a.kind !== "call" : a.kind === "call";
  });
  const actDate = (at: number): string => {
    if (fmtHistoryDay(at, now) === "Today") return fmtClock(at);
    const d = new Date(at);
    const z = (n: number) => (n < 10 ? "0" + n : "" + n);
    return z(d.getDate()) + "/" + z(d.getMonth() + 1);
  };
  const markAllActivityRead = () => {
    void notifications.markAllRead();
    void chat.markAllRead();
    callActions().markRead();
    setPop(null);
    toast("Everything marked as read");
  };
  const actCurrent = actItems.filter((a) => a.id === actSel)[0] || null;
  const activityBadge = notifUnread + missedUnread;

  /** Open what a notification points at: "/chat/<conversationId>", a meeting, or nothing in particular. */
  const goTo = useCallback(
    (link: string | undefined) => {
      if (!link) return;
      const chatMatch = /^\/chat\/([^/?#]+)/.exec(link);
      if (chatMatch?.[1]) {
        const convId = decodeURIComponent(chatMatch[1]);
        const partner = Object.keys(dmOf).find((uid) => dmOf[uid] === convId);
        const c = partner ? contactById[partner] : undefined;
        if (c) openChat(c);
        return;
      }
      const meetMatch = /^\/meetings\/([^/?#]+)/.exec(link);
      if (meetMatch?.[1]) {
        openMeeting({ meetingId: decodeURIComponent(meetMatch[1]) });
        return;
      }
      if (link.startsWith("/meetings")) setPage("meetings");
    },
    [dmOf, contactById, openChat],
  );

  const openActivity = (a: ActItem) => {
    setActSel(a.id);
    if (a.notifId) void notifications.markRead(a.notifId);
    if (a.kind === "message" && a.contact) {
      openChat(a.contact);
      setPage("activity");
    } else if (a.kind === "other") {
      goTo(a.link);
    }
    if (a.kind === "call" && a.callId) callActions().markRead(a.callId);
  };

  /* a click on a push / browser notification lands here */
  useEffect(
    () =>
      onNavigate((link) => {
        setIsOpen(true);
        goTo(link);
      }),
    [goTo, setIsOpen],
  );

  /* ---------------- top search ---------------- */
  const tq = topQuery.trim().toLowerCase();
  const topResults = tq
    ? contactList.filter((c) => `${fullName(c)} ${c.email} ${c.phoneNumber}`.toLowerCase().indexOf(tq) !== -1).slice(0, 6)
    : [];

  /* the directory is searched on the server (debounced), so people beyond the first page are findable */
  const searchTimer = useRef<number | undefined>(undefined);
  const searchDirectory = useCallback((q: string) => {
    window.clearTimeout(searchTimer.current);
    if (q.trim().length < 2) return;
    searchTimer.current = window.setTimeout(() => void chat.loadDirectory(q.trim()), 250);
  }, []);
  useEffect(() => () => window.clearTimeout(searchTimer.current), []);

  const onWindowKey = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      if (pop) {
        setPop(null);
        return;
      }
      setIsOpen(false);
    }
  };

  /* people who signed in after this page loaded: refresh the directory whenever a people picker opens */
  useEffect(() => {
    if (pop === "newChat" || pop === "topsearch") void chat.loadDirectory();
  }, [pop]);

  const popToggle = (id: string) => setPop((p) => (p === id ? null : id));

  /* ---------------- sub-views ---------------- */
  const renderRow = (c: Convo) => {
    const on = selectedContact?._id === c.contact._id && page === "chat";
    const isFav = favs.indexOf(c.contact._id) !== -1;
    const popId = "row:" + c.contact._id;
    const dr = drafts[c.contact._id];
    return (
      <div key={c.contact._id} className={`${styles.chatRow} ${on ? styles.chatRowOn : ""}`} data-pop={popId}>
        <button type="button" className={styles.chatRowMain} onClick={() => openChat(c.contact)} aria-current={on ? "true" : undefined}>
          <PersonAvatar name={fullName(c.contact)} image={c.contact.profilePicture} size={32} online={c.contact.status === true} />
          <span className={styles.chatRowText}>
            <span className={`${styles.chatRowName} ${c.unread ? styles.strong : ""}`}>{fullName(c.contact)}</span>
            <span className={styles.chatRowPrev}>
              {dr && dr.trim() ? (
                <>
                  <em className={styles.draftTag}>Draft:</em> {dr}
                </>
              ) : c.preview ? (
                `${c.mine ? "You: " : ""}${c.preview}`
              ) : (
                "No messages yet"
              )}
            </span>
          </span>
          {c.unread > 0 && <span className={styles.dotBadge}>{c.unread > 99 ? "99+" : c.unread}</span>}
        </button>
        <button type="button" className={styles.chatRowMore} aria-label={`More options for ${fullName(c.contact)}`} aria-haspopup="menu" aria-expanded={pop === popId} onClick={() => popToggle(popId)}>
          <MoreHorizontal20Regular />
        </button>
        {pop === popId && (
          <div className={`${styles.pop} ${styles.popRowMenu}`} role="menu">
            <button type="button" role="menuitem" className={styles.popItem} onClick={() => { setFavs((f) => (isFav ? f.filter((x) => x !== c.contact._id) : f.concat([c.contact._id]))); setPop(null); }}>
              {isFav ? <PinOff20Regular /> : <Pin20Regular />}
              {isFav ? "Unpin" : "Pin"}
            </button>
            <button
              type="button"
              role="menuitem"
              className={styles.popItem}
              onClick={() => {
                const id = dmOf[c.contact._id];
                if (id) void chat.markRead(id);
                setPop(null);
              }}
            >
              <Checkmark16Regular />
              Mark as read
            </button>
          </div>
        )}
      </div>
    );
  };

  const section = (key: string, title: string, items: Convo[]) =>
    items.length === 0 ? null : (
      <div key={key} className={styles.listSection}>
        <button type="button" className={styles.sectionBtn} aria-expanded={secOpen[key] !== false} onClick={() => setSecOpen({ ...secOpen, [key]: secOpen[key] === false })}>
          <ChevronDown12Regular style={{ transform: secOpen[key] === false ? "rotate(-90deg)" : "none" }} />
          {title}
        </button>
        {secOpen[key] !== false && items.map(renderRow)}
      </div>
    );

  const chatList = (
    <aside className={styles.chatList} aria-label="Chats">
      <div className={styles.listHead}>
        <h2 className={styles.listTitle}>Chat</h2>
        <span className={styles.grow} />
        <div className={styles.anchor} data-pop="listMore">
          <button type="button" className={styles.iconBtn} aria-label="More chat options" aria-haspopup="menu" aria-expanded={pop === "listMore"} onClick={() => popToggle("listMore")}>
            <MoreHorizontal20Regular />
          </button>
          {pop === "listMore" && (
            <div className={`${styles.pop} ${styles.popRight}`} role="menu">
              <button type="button" role="menuitem" className={styles.popItem} onClick={() => { void chat.markAllRead(); setPop(null); toast("All chats marked as read"); }}>
                <Checkmark16Regular />
                Mark all as read
              </button>
            </div>
          )}
        </div>
        <button type="button" className={`${styles.iconBtn} ${listSearchOn ? styles.iconBtnOn : ""}`} aria-label="Search chats" aria-pressed={listSearchOn} onClick={() => { setListSearchOn(!listSearchOn); if (listSearchOn) setListSearch(""); }}>
          <Search20Regular />
        </button>
        <div className={styles.anchor} data-pop="newChat">
          <button type="button" className={styles.iconBtn} aria-label="New chat" aria-haspopup="dialog" aria-expanded={pop === "newChat"} onClick={() => popToggle("newChat")}>
            <Compose20Regular />
            <ChevronDown12Regular />
          </button>
          {pop === "newChat" && <NewChatPop contacts={contactList} onPick={openChat} onQuery={searchDirectory} />}
        </div>
      </div>

      {listSearchOn && (
        <div className={styles.listSearch}>
          <Search20Regular />
          <input autoFocus value={listSearch} placeholder="Search chats and contacts" aria-label="Search chats and contacts" onChange={(e) => setListSearch(e.target.value)} />
        </div>
      )}

      <div className={styles.pills} role="group" aria-label="Filter chats">
        {(
          [
            ["unread", "Unread"],
            ["chats", "Chats"],
            ["contacts", "Contacts"],
          ] as [ListFilter, string][]
        ).map(([k, label]) => (
          <button key={k} type="button" aria-pressed={listFilter === k} className={`${styles.pill} ${listFilter === k ? styles.pillOn : ""}`} onClick={() => setListFilter(listFilter === k ? "all" : k)}>
            {label}
          </button>
        ))}
      </div>

      <div className={styles.listScroll}>
        {listFilter === "contacts" ? (
          <>
            {contactGroups.length === 0 && <div className={styles.emptyNote}>No contacts found</div>}
            {contactGroups.map((g) => (
              <div key={g.letter}>
                <div className={styles.letter}>{g.letter}</div>
                {g.items.map((c) => (
                  <button key={c._id} type="button" className={styles.contactRow} onClick={() => openChat(c)}>
                    <PersonAvatar name={fullName(c)} image={c.profilePicture} size={32} online={c.status === true} />
                    <span className={styles.chatRowText}>
                      <span className={styles.chatRowName}>{fullName(c)}</span>
                      <span className={styles.chatRowPrev}>{c.email}</span>
                    </span>
                  </button>
                ))}
              </div>
            ))}
          </>
        ) : (
          <>
            {section("fav", "Favourites", favConvos)}
            {section("chats", "Chats", chatConvos)}
            {visibleConvos.length === 0 && (
              <div className={styles.emptyNote}>
                {convos.length === 0 ? "No conversations yet. Start a new chat with the pencil above." : listFilter === "unread" ? "You're all caught up" : "No chats found"}
              </div>
            )}
          </>
        )}
      </div>
    </aside>
  );

  const conversation = (c: IContact) => {
    const name = fullName(c);
    const isFav = favs.indexOf(c._id) !== -1;
    return (
      <section className={styles.convo} aria-label={`Chat with ${name}`}>
        <header className={styles.convoHead}>
          <PersonAvatar name={name} image={c.profilePicture} size={32} online={c.status === true} />
          <h2 className={styles.convoName}>{name}</h2>
          <div className={styles.headTabs} role="tablist">
            <button type="button" role="tab" aria-selected={convoTab === "chat"} className={`${styles.headTab} ${convoTab === "chat" ? styles.headTabOn : ""}`} onClick={() => setConvoTab("chat")}>
              Chat
            </button>
            <button type="button" role="tab" aria-selected={convoTab === "shared"} className={`${styles.headTab} ${convoTab === "shared" ? styles.headTabOn : ""}`} onClick={() => setConvoTab("shared")}>
              Shared
            </button>
          </div>
          <span className={styles.grow} />
          <div className={styles.splitIcon}>
            <button type="button" className={styles.iconBtn} aria-label={`Call ${name}`} title="Call" onClick={() => callContact(c)}>
              <Call20Regular />
            </button>
            <div className={styles.anchor} data-pop="callMenu">
              <button type="button" className={`${styles.iconBtn} ${styles.iconBtnSm}`} aria-label="Call options" aria-haspopup="menu" aria-expanded={pop === "callMenu"} onClick={() => popToggle("callMenu")}>
                <ChevronDown12Regular />
              </button>
              {pop === "callMenu" && (
                <div className={`${styles.pop} ${styles.popRight}`} role="menu">
                  <button type="button" role="menuitem" className={styles.popItem} onClick={() => callContact(c)}>
                    <Call20Regular />
                    Audio call
                  </button>
                  <button type="button" role="menuitem" className={styles.popItem} onClick={() => callContact(c, true)}>
                    <Call20Filled />
                    Video call
                  </button>
                </div>
              )}
            </div>
          </div>
          <button type="button" className={styles.iconBtn} aria-label="Add people" title="Add people" onClick={() => toast("Group chats aren't available yet")}>
            <PeopleAdd20Regular />
          </button>
          <span className={styles.barRule} />
          <button type="button" className={`${styles.iconBtn} ${convoSearchOn ? styles.iconBtnOn : ""}`} aria-label="Search in chat" aria-pressed={convoSearchOn} title="Search in chat" onClick={() => { setConvoSearchOn(!convoSearchOn); setConvoSearch(""); setConvoTab("chat"); }}>
            <Search20Regular />
          </button>
          <div className={styles.anchor} data-pop="convoMore">
            <button type="button" className={styles.iconBtn} aria-label="More options" aria-haspopup="menu" aria-expanded={pop === "convoMore"} onClick={() => popToggle("convoMore")}>
              <MoreHorizontal20Regular />
            </button>
            {pop === "convoMore" && (
              <div className={`${styles.pop} ${styles.popRight}`} role="menu">
                <button type="button" role="menuitem" className={styles.popItem} onClick={() => { setFavs((f) => (isFav ? f.filter((x) => x !== c._id) : f.concat([c._id]))); setPop(null); }}>
                  {isFav ? <PinOff20Regular /> : <Pin20Regular />}
                  {isFav ? "Unpin chat" : "Pin chat"}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className={styles.popItem}
                  onClick={() => {
                    try {
                      if (navigator.clipboard) navigator.clipboard.writeText(c.email).catch(() => undefined);
                    } catch {
                      /* clipboard unavailable */
                    }
                    setPop(null);
                    toast("Email address copied");
                  }}
                >
                  Copy email address
                </button>
              </div>
            )}
          </div>
        </header>

        {convoSearchOn && (
          <div className={styles.convoSearch}>
            <Search20Regular />
            <input autoFocus value={convoSearch} placeholder="Search in this chat" aria-label="Search in this chat" onChange={(e) => setConvoSearch(e.target.value)} />
            <button type="button" className={styles.iconBtn} aria-label="Close search" onClick={() => { setConvoSearchOn(false); setConvoSearch(""); }}>
              <Dismiss20Regular />
            </button>
          </div>
        )}

        {convoTab === "chat" ? (
          <>
            <div className={styles.msgs} ref={msgsRef}>
              {hasMoreFor(c._id) && !convoSearch && (
                <div style={{ display: "flex", justifyContent: "center", padding: "8px 0" }}>
                  <button type="button" className={styles.outlineBtn} onClick={() => { const id = dmOf[c._id]; if (id) chat.loadHistory(id).catch(() => toast("Couldn't load earlier messages")); }}>
                    Load earlier messages
                  </button>
                </div>
              )}
              {rows.length === 0 && (
                <div className={styles.convoEmpty}>
                  <PersonAvatar name={name} image={c.profilePicture} size={72} />
                  <strong>{name}</strong>
                  <span>{convoSearch ? "No messages match your search" : "This is the beginning of your chat. Say hello 👋"}</span>
                </div>
              )}
              {rows.map((r) =>
                r.type === "day" ? (
                  <div key={r.key} className={styles.dayDivider}>
                    <span>{r.label}</span>
                  </div>
                ) : (
                  <div key={r.key} className={`${styles.group} ${r.mine ? styles.groupMine : ""}`}>
                    {!r.mine && <PersonAvatar name={r.name} image={r.image} size={32} />}
                    <div className={styles.groupBody}>
                      <div className={styles.groupMeta}>
                        {!r.mine && <span className={styles.groupName}>{r.name}</span>}
                        <span>{r.stamp}</span>
                      </div>
                      {r.items.map((m, i) => {
                        const isLast = r.mine && m === lastMsg;
                        return (
                          <div key={m.messageId + "-" + i} className={styles.bubbleRow}>
                            <div className={styles.bubble} style={m.sendState === "sending" ? { opacity: 0.6 } : undefined}>{m.message}</div>
                            {m.sendState === "failed" && (
                              <button type="button" className={styles.outlineBtn} onClick={() => { const id = dmOf[c._id]; if (id && m.clientMsgId) void chat.deliver(id, m.clientMsgId); }}>
                                Not sent. Retry
                              </button>
                            )}
                            {isLast && m.sendState !== "failed" && (
                              <span className={styles.seen} title={m.read ? "Seen" : "Sent"}>
                                {m.read ? <Eye16Filled /> : <CheckmarkCircle16Regular />}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ),
              )}
            </div>

            <div aria-live="polite" style={{ minHeight: 18, padding: "0 20px", fontSize: 12, color: "#616161" }}>
              {typingNames(c._id) ? `${typingNames(c._id)} is typing…` : ""}
            </div>
            <div className={styles.composerWrap}>
              <div className={styles.composer}>
                <textarea
                  ref={inputRef}
                  rows={1}
                  value={draft}
                  placeholder="Type a message"
                  aria-label={`Message ${name}`}
                  spellCheck={false}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                      e.preventDefault();
                      handleSendMessage(draft);
                    }
                  }}
                />
                <div className={styles.tools}>
                  <button type="button" className={styles.iconBtn} aria-label="Format" title="Format" onClick={() => toast("Text formatting isn't available yet")}>
                    <TextEditStyle20Regular />
                  </button>
                  <div className={styles.anchor} data-pop="emoji">
                    <button type="button" className={styles.iconBtn} aria-label="Emoji" title="Emoji" aria-haspopup="dialog" aria-expanded={pop === "emoji"} onClick={() => popToggle("emoji")}>
                      <Emoji20Regular />
                    </button>
                    {pop === "emoji" && (
                      <div className={`${styles.pop} ${styles.popUp} ${styles.popRight} ${styles.emojiGrid}`} role="dialog" aria-label="Emoji">
                        {EMOJIS.map((em) => (
                          <button
                            key={em}
                            type="button"
                            className={styles.emojiBtn}
                            aria-label={em}
                            onClick={() => {
                              setDraft(draft + em);
                              setPop(null);
                              if (inputRef.current) inputRef.current.focus();
                            }}
                          >
                            {em}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button type="button" className={styles.iconBtn} aria-label="Attach" title="Attach" onClick={() => toast("File sharing isn't available yet")}>
                    <Attach20Regular />
                  </button>
                  <button type="button" className={styles.iconBtn} aria-label="More" title="More" onClick={() => toast("More actions aren't available yet")}>
                    <Add20Regular />
                  </button>
                  <span className={styles.barRule} />
                  <button type="button" className={`${styles.sendBtn} ${draft.trim() ? styles.sendOn : ""}`} aria-label="Send" title="Send" disabled={!draft.trim()} onClick={() => handleSendMessage(draft)}>
                    {draft.trim() ? <Send20Filled /> : <Send20Regular />}
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className={styles.shared}>
            {sharedFiles.length === 0 && <div className={styles.convoEmpty}><Document20Regular /><span>No files have been shared in this chat</span></div>}
            {sharedFiles.map((m, i) => (
              <a key={i} className={styles.fileRow} href={m.fileUrl || "#"} target="_blank" rel="noreferrer">
                <Document20Regular />
                <span>{m.fileUrl ? m.fileUrl.split("/").pop() : "File"}</span>
                <small>{fmtStamp(ms(m.timestamp), now)}</small>
              </a>
            ))}
          </div>
        )}
      </section>
    );
  };

  const chatEmpty = (
    <section className={`${styles.convo} ${styles.convoNone}`} aria-label="No chat selected">
      <ChatMultiple24Regular />
      <strong>Select a chat to start messaging</strong>
      <span>Pick a conversation on the left, or start a new one with the pencil.</span>
    </section>
  );

  const activityPage = (
    <div className={styles.chatPage}>
      <aside className={`${styles.chatList} ${styles.chatListWide}`} aria-label="Activity">
        <div className={styles.listHead}>
          <h2 className={styles.listTitle}>Activity</h2>
          <span className={styles.grow} />
          <div className={styles.anchor} data-pop="actMore">
            <button type="button" className={styles.iconBtn} aria-label="More activity options" aria-haspopup="menu" aria-expanded={pop === "actMore"} onClick={() => popToggle("actMore")}>
              <MoreHorizontal20Regular />
            </button>
            {pop === "actMore" && (
              <div className={`${styles.pop} ${styles.popRight}`} role="menu">
                <button type="button" role="menuitem" className={styles.popItem} onClick={markAllActivityRead}>
                  <Checkmark16Regular />
                  Mark all as read
                </button>
              </div>
            )}
          </div>
          <button type="button" className={`${styles.iconBtn} ${actSearchOn ? styles.iconBtnOn : ""}`} aria-label="Search activity" aria-pressed={actSearchOn} onClick={() => { setActSearchOn(!actSearchOn); if (actSearchOn) setActSearch(""); }}>
            <Search20Regular />
          </button>
        </div>
        {actSearchOn && (
          <div className={styles.listSearch}>
            <Search20Regular />
            <input autoFocus value={actSearch} placeholder="Search activity" aria-label="Search activity" onChange={(e) => setActSearch(e.target.value)} />
          </div>
        )}
        <div className={styles.pills} role="group" aria-label="Filter activity">
          {(
            [
              ["unread", "Unread"],
              ["messages", "Messages"],
              ["calls", "Calls"],
            ] as ["unread" | "messages" | "calls", string][]
          ).map(([k, label]) => (
            <button key={k} type="button" aria-pressed={actFilter === k} className={`${styles.pill} ${actFilter === k ? styles.pillOn : ""}`} onClick={() => setActFilter(actFilter === k ? "all" : k)}>
              {label}
            </button>
          ))}
        </div>
        <div className={styles.listScroll}>
          {actShown.length === 0 && <div className={styles.emptyNote}>{actFilter === "unread" ? "You're all caught up" : "Nothing here yet"}</div>}
          {actShown.map((a) => (
            <div key={a.id} className={`${styles.actItem} ${actSel === a.id ? styles.actOn : ""}`}>
              <button type="button" className={styles.actMain} onClick={() => openActivity(a)}>
                <span className={`${styles.unreadDot} ${a.unread ? styles.unreadDotOn : ""}`} aria-hidden="true" />
                <span className={styles.actAvatar}>
                  <PersonAvatar name={a.name} image={a.image} size={40} />
                  <span className={`${styles.actBadge} ${a.kind === "call" ? styles.actBadgeMissed : styles.actBadgeMsg}`} aria-hidden="true">
                    {a.kind === "call" ? <CallMissed16Regular /> : <Chat16Filled />}
                  </span>
                </span>
                <span className={styles.actText}>
                  <span className={styles.actTop}>
                    <span className={`${styles.actTitle} ${a.unread ? styles.strong : ""}`}>{a.title}</span>
                    <span className={styles.actWhen}>{actDate(a.at)}</span>
                  </span>
                  {a.sub && <span className={styles.actSub}>{a.sub}</span>}
                  {a.context && <span className={styles.actCtx}>{a.context}</span>}
                </span>
              </button>
              {a.kind === "call" && a.contact && (
                <div className={styles.actBtns}>
                  <button type="button" className={styles.outlineBtn} onClick={() => a.contact && callContact(a.contact)}>
                    Call
                  </button>
                  <button type="button" className={styles.outlineBtn} onClick={() => a.contact && openChat(a.contact)}>
                    Chat
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </aside>

      {actCurrent && actCurrent.kind === "message" && actCurrent.contact && selectedContact && selectedContact._id === actCurrent.contact._id ? (
        conversation(selectedContact)
      ) : (
        <ActivityCalls
          calls={calls}
          now={now}
          onCall={(c) => {
            const ct = contactById[c.contactId];
            if (ct) callContact(ct);
          }}
          onChat={(c) => {
            const ct = contactById[c.contactId];
            if (ct) openChat(ct);
          }}
          onOpenCalls={() => { setSpeedReq(Date.now()); setPage("calls"); setPop(null); }}
          onViewContacts={() => { setPage("chat"); setListOpen(true); setListFilter("contacts"); }}
        />
      )}
    </div>
  );

  const badgeFor = (k: Page): number => (k === "chat" ? totalUnread : k === "activity" ? activityBadge : 0);

  /* ---------------- render ---------------- */
  return (
    <>
      {/* floating chat button (unchanged) */}
     <FloatingChatButton
      name={myName}
      image={user.profilePicture}
      count={totalUnread + missedUnread}
      onClick={() => setIsOpen(true)}
      hidden={isOpen}
    />

      <CallOverlay
        windowOpen={isOpen}
        page={page}
        onOpen={() => setIsOpen(true)}
        onShowCall={() => { setIsOpen(true); setPage("calls"); }}
        contactById={contactById}
        toast={toast}
      />
      <NotificationSettings open={settingsOpen} onClose={() => setSettingsOpen(false)} toast={toast} />

      {isOpen &&
        createPortal(
          <div className={styles.backdrop} onMouseDown={(e) => e.target === e.currentTarget && setIsOpen(false)}>
            <FluentProvider theme={webLightTheme} className={`${styles.window} ${sizeStyles.big}`} role="dialog" aria-modal="true" aria-label="Messaging" tabIndex={-1} ref={windowRef} onKeyDown={onWindowKey}>
              {/* ───────── title bar ───────── */}
              <div className={styles.topBar}>
                <div className={styles.topLeft}>
                  <button type="button" className={styles.topIcon} aria-label={listOpen ? "Hide chat list" : "Show chat list"} title="Toggle list" onClick={() => { setListOpen(!listOpen); setPage("chat"); }}>
                    <PanelLeft24Regular />
                  </button>
                </div>
                <div className={styles.searchWrap} data-pop="topsearch">
                  <Search20Regular />
                  <input
                    value={topQuery}
                    placeholder="Search people"
                    aria-label="Search people"
                    onFocus={() => setPop("topsearch")}
                    onChange={(e) => { setTopQuery(e.target.value); setPop("topsearch"); searchDirectory(e.target.value); }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && topResults[0]) {
                        openChat(topResults[0]);
                        setTopQuery("");
                      }
                    }}
                  />
                  {pop === "topsearch" && tq && (
                    <div className={`${styles.pop} ${styles.searchPop}`} role="listbox" aria-label="People">
                      {topResults.length === 0 && <div className={styles.emptyNote}>No people found</div>}
                      {topResults.map((c) => (
                        <button key={c._id} type="button" role="option" aria-selected={false} className={styles.popItem} onClick={() => { openChat(c); setTopQuery(""); }}>
                          <PersonAvatar name={fullName(c)} image={c.profilePicture} size={24} online={c.status === true} />
                          <span className={styles.suggestText}>
                            <span>{fullName(c)}</span>
                            <small>{c.email}</small>
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className={styles.topRight}>
                  <div className={styles.anchor} data-pop="topMore">
                    <button type="button" className={styles.topIcon} aria-label="Settings and more" aria-haspopup="menu" aria-expanded={pop === "topMore"} onClick={() => popToggle("topMore")}>
                      <MoreHorizontal20Regular />
                    </button>
                    {pop === "topMore" && (
                      <div className={`${styles.pop} ${styles.popRight}`} role="menu">
                        <button type="button" role="menuitem" className={styles.popItem} onClick={() => { setPop(null); setSettingsOpen(true); }}>
                          <Settings20Regular />
                          Settings
                        </button>
                        <button type="button" role="menuitem" className={styles.popItem} onClick={() => { setPop(null); toast("Help isn't available yet"); }}>
                          <QuestionCircle20Regular />
                          Help
                        </button>
                        <button type="button" role="menuitem" className={styles.popItem} onClick={() => { setPop(null); toast("The mobile app link isn't set up yet"); }}>
                          <Phone20Regular />
                          Download the mobile app
                        </button>
                        {logout && (
                          <button type="button" role="menuitem" className={styles.popItem} onClick={() => { setPop(null); logout(); }}>
                            <Person20Regular />
                            Sign out
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <span className={styles.me} title={`${myName} · Available`}>
                    <PersonAvatar name={myName || "Me"} image={user.profilePicture} size={28} online={true} />
                  </span>
                  <button type="button" className={styles.topIcon} aria-label="Close" title="Close" onClick={() => setIsOpen(false)}>
                    <Dismiss20Regular />
                  </button>
                </div>
              </div>

              {/* ───────── rail + pages ───────── */}
              <div className={styles.body}>
                <nav className={styles.rail} aria-label="Apps">
                  {RAIL.map((r) => {
                    const on = page === r.key;
                    const Ic = on ? r.IconOn : r.Icon;
                    const n = badgeFor(r.key);
                    return (
                      <button key={r.key} type="button" className={`${styles.railBtn} ${on ? styles.railOn : ""}`} aria-label={r.label} aria-current={on ? "page" : undefined} title={r.label} onClick={() => setPage(r.key)}>
                        <Ic />
                        {n > 0 && <span className={styles.railBadge}>{n > 99 ? "99+" : n}</span>}
                      </button>
                    );
                  })}
                  {/* Add apps – not available yet
                  <button type="button" className={styles.railBtn} aria-label="Apps" title="Apps" onClick={() => toast("Add apps isn't available yet")}>
                    <AppsAddIn24Regular />
                  </button>
                  */}
                  {/* More – not available yet
                  <button type="button" className={styles.railBtn} aria-label="More" title="More" onClick={() => toast("No more apps yet")}>
                    <MoreHorizontal24Regular />
                  </button>
                  */}
                </nav>

                <main className={styles.pages}>
                  {page === "chat" && (
                    <div className={styles.chatPage}>
                      {listOpen && chatList}
                      {selectedContact ? conversation(selectedContact) : chatEmpty}
                    </div>
                  )}
                  {page === "activity" && activityPage}
                  <div className={styles.pageFill} style={{ display: page === "calls" ? "flex" : "none" }}>
                    <TeamsCalls
                      contacts={contactList}
                      calls={calls}
                      setCalls={setCalls}
                      onChat={openChat}
                      onViewContacts={() => { setPage("chat"); setListOpen(true); setListFilter("contacts"); }}
                      toast={toast}
                      dial={dialReq}
                      speedAdd={speedReq}
                      userId={user.userId}
                    />
                  </div>
                  <div className={styles.pageFill} style={{ display: page === "calendar" ? "flex" : "none" }}>
                    <TeamsCalendar ownerName={myName || "Me"} toast={toast} visible={page === "calendar"} directory={directory} onJoin={(ev) => openMeeting({ joinUrl: ev.onlineUrl })}
                      onChat={(ev) => {
                        const org = contactList.find((c) => fullName(c).toLowerCase() === ev.organizer.trim().toLowerCase());
                        if (org) openChat(org);
                        else toast("Couldn't find the organiser to chat with.");
                      }}
                    />
                  </div>
                  <div className={styles.pageFill} style={{ display: page === "meetings" ? "flex" : "none" }}>
                    {page === "meetings" && <MeetingsPage />}
                  </div>
                </main>
              </div>

              {toastMsg && (
                <div key={toastMsg.id} className={styles.toast} role="status">
                  {toastMsg.msg}
                </div>
              )}
            </FluentProvider>
          </div>,
          document.body,
        )}
    </>
  );
};

/* ---------- calls panel shown on the Activity page (missed-call view) ---------- */
type CallFilter = "all" | "missed" | "incoming" | "outgoing";
const CALL_FILTERS: [CallFilter, string][] = [
  ["all", "All"],
  ["missed", "Missed"],
  ["incoming", "Incoming"],
  ["outgoing", "Outgoing"],
];
const callDirLabel = (d: CallEntry["direction"]) => (d === "missed" ? "Missed incoming" : d === "incoming" ? "Incoming" : "Outgoing");

const ActivityCalls: React.FC<{
  calls: CallEntry[];
  now: number;
  onCall: (c: CallEntry) => void;
  onChat: (c: CallEntry) => void;
  onOpenCalls: () => void;
  onViewContacts: () => void;
}> = ({ calls, now, onCall, onChat, onOpenCalls, onViewContacts }) => {
  const [filter, setFilter] = useState<CallFilter>("missed");
  const [newestFirst, setNewestFirst] = useState(true);
  const [menu, setMenu] = useState<string | null>(null);
  const closeMenu = useCallback(() => setMenu(null), []);
  useOutsideClose(menu, closeMenu);

  const shown = calls
    .filter((c) => filter === "all" || c.direction === filter)
    .slice()
    .sort((a, b) => (newestFirst ? b.at - a.at : a.at - b.at));
  const label = (CALL_FILTERS.filter((f) => f[0] === filter)[0] || CALL_FILTERS[0] || ["all", "All"])[1];

  return (
    <div className={styles.callsPage}>
      <div className={styles.pageHead}>
        <span className={styles.appTile}>
          <Call20Filled />
        </span>
        <h2 className={styles.pageTitle}>Calls</h2>
        <div className={styles.headTabs} role="tablist">
          <button type="button" role="tab" aria-selected="true" className={`${styles.headTab} ${styles.headTabOn}`}>
            Personal
          </button>
        </div>
        <span className={styles.grow} />
        <button type="button" className={styles.outlineBtn} onClick={onViewContacts}>
          View contacts
          <Open16Regular />
        </button>
        <button type="button" className={`${styles.iconBtn} ${styles.iconBtnBrand}`} aria-label="Contacts" title="Contacts" onClick={onViewContacts}>
          <People20Filled />
        </button>
      </div>

      <div className={`${styles.callsBody} ${styles.callsBodyCompact}`}>
        <div className={styles.history}>
          <div className={styles.sectionHead}>
            <span className={styles.sectionTitle}>
              <ChevronDown12Regular />
              History
            </span>
            <span className={styles.grow} />
            <div className={styles.anchor} data-pop="actCallFilter">
              <button type="button" className={styles.pillBrand} aria-haspopup="menu" aria-expanded={menu === "actCallFilter"} onClick={() => setMenu(menu === "actCallFilter" ? null : "actCallFilter")}>
                {label}
                <ChevronDown12Regular />
              </button>
              {menu === "actCallFilter" && (
                <div className={`${styles.pop} ${styles.popRight}`} role="menu">
                  {CALL_FILTERS.map(([k, text]) => (
                    <button key={k} type="button" role="menuitemradio" aria-checked={filter === k} className={styles.popItem} onClick={() => { setFilter(k); setMenu(null); }}>
                      <span className={styles.tick}>{filter === k && <Checkmark16Regular />}</span>
                      {text}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button type="button" className={styles.iconBtn} aria-label={newestFirst ? "Showing newest first" : "Showing oldest first"} title={newestFirst ? "Newest first" : "Oldest first"} onClick={() => setNewestFirst(!newestFirst)}>
              <Filter20Regular />
            </button>
          </div>
          <div className={styles.historyList} role="list" aria-label="Call history">
            {shown.length === 0 && <div className={styles.emptyNote}>{calls.length === 0 ? "No calls yet" : "No calls match this filter"}</div>}
            {shown.map((c) => {
              const Dir = c.direction === "missed" ? CallMissed16Regular : c.direction === "incoming" ? CallInbound16Regular : CallOutbound16Regular;
              return (
                <div key={c.id} role="listitem" className={styles.callRow}>
                  <PersonAvatar name={c.name} image={c.image} size={32} online={c.online} />
                  <div className={styles.callMain}>
                    <div className={`${styles.callName} ${!c.read ? styles.strong : ""}`}>{c.name}</div>
                    <div className={`${styles.callSub} ${c.direction === "missed" ? styles.missed : ""}`}>
                      <Dir />
                      {callDirLabel(c.direction)}
                    </div>
                  </div>
                  <div className={styles.callActions}>
                    <button type="button" className={styles.iconBtn} aria-label={`Call ${c.name}`} title="Call" onClick={() => onCall(c)}>
                      <Call20Regular />
                    </button>
                    <button type="button" className={styles.iconBtn} aria-label={`Chat with ${c.name}`} title="Chat" onClick={() => onChat(c)}>
                      <Chat20Regular />
                    </button>
                  </div>
                  <div className={styles.callWhen}>
                    <span>{fmtHistoryDay(c.at, now)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.speed}>
          <div className={styles.sectionHead}>
            <span className={styles.sectionTitleLg}>Speed dial</span>
            <span className={styles.grow} />
            <button type="button" className={styles.iconBtn} aria-label="Manage speed dial" title="Manage speed dial" onClick={onOpenCalls}>
              <PersonAdd20Regular />
            </button>
          </div>
          <div className={styles.speedEmpty}>
            <div className={styles.illus} aria-hidden="true">
              <span className={`${styles.orb} ${styles.orb1}`}>
                <Person20Filled />
              </span>
              <span className={`${styles.orb} ${styles.orb2}`}>
                <Person20Filled />
              </span>
              <span className={`${styles.orb} ${styles.orb3}`}>
                <Add16Regular />
              </span>
            </div>
            <p className={styles.speedText}>Add people to speed dial for quick access.</p>
            <button type="button" className={styles.outlineBtn} onClick={onOpenCalls}>
              <PersonAdd20Regular />
              Add
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------- "new chat" popover ---------- */
const NewChatPop: React.FC<{ contacts: IContact[]; onPick: (c: IContact) => void; onQuery?: (q: string) => void }> = ({ contacts, onPick, onQuery }) => {
  const [q, setQ] = useState("");
  const t = q.trim().toLowerCase();
  const list = contacts.filter((c) => !t || `${fullName(c)} ${c.email}`.toLowerCase().indexOf(t) !== -1).slice(0, 30);
  return (
    <div className={`${styles.pop} ${styles.popRight} ${styles.newChatPop}`} role="dialog" aria-label="New chat">
      <div className={styles.listSearch}>
        <Person20Regular />
        <input autoFocus value={q} placeholder="To: type a name" aria-label="To" onChange={(e) => { setQ(e.target.value); if (onQuery) onQuery(e.target.value); }} />
      </div>
      <div className={styles.popScroll}>
        {list.length === 0 && <div className={styles.emptyNote}>No people found</div>}
        {list.map((c) => (
          <button key={c._id} type="button" className={styles.popItem} onClick={() => onPick(c)}>
            <PersonAvatar name={fullName(c)} image={c.profilePicture} size={24} online={c.status === true} />
            <span className={styles.suggestText}>
              <span>{fullName(c)}</span>
              <small>{c.email}</small>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default InternalCommunication;